import { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import api from '../lib/api';

const SESSION_KEY = 'lc_ai_session';
const GREETING = 'Ciao! 👋 Sono l\'assistente di La Contadina. Posso aiutarti con orari, giorni di apertura e informazioni sulle prenotazioni. In cosa posso esserti utile? (IT / DE / EN)';

export const AiChatWidget = ({ enabled, restaurantName }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  if (!enabled) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setSending(true);
    try {
      const { data } = await api.post('/ai-assistant/chat', { message: text, session_id: sessionId });
      if (data.enabled === false) {
        setUnavailable(true);
        setMessages((m) => [...m, { role: 'assistant', content: data.message || 'Al momento non disponibile.' }]);
      } else {
        if (data.session_id && data.session_id !== sessionId) {
          setSessionId(data.session_id);
          localStorage.setItem(SESSION_KEY, data.session_id);
        }
        setMessages((m) => [...m, { role: 'assistant', content: data.reply || '…' }]);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: 'Ops, qualcosa è andato storto. Riprova tra poco o usa il modulo di prenotazione.' }]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="ai-chat-launcher"
        aria-label="Assistente chat"
        className="fixed bottom-5 right-5 z-[60] flex items-center justify-center w-14 h-14 rounded-full bg-[#1C1C19] text-white shadow-lg hover:scale-105 active:scale-95 transition-transform duration-200"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] max-w-sm h-[70vh] max-h-[560px] flex flex-col bg-white rounded-2xl shadow-2xl border border-[#E5E5E0] overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-[#1C1C19] text-white shrink-0">
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white/10">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <p className="text-sm font-semibold">{restaurantName || 'La Contadina'}</p>
              <p className="text-xs text-white/60">Assistente · IT / DE / EN</p>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} data-testid="ai-chat-messages" className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-[#FAFAF8]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  data-testid={`ai-chat-msg-${m.role}`}
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-[#1C1C19] text-white rounded-br-sm'
                      : 'bg-white text-[#1C1C19] border border-[#E5E5E0] rounded-bl-sm'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-3.5 py-2.5 rounded-2xl bg-white border border-[#E5E5E0] text-[#5C5C59]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-[#E5E5E0] p-2.5 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                disabled={unavailable}
                placeholder={unavailable ? 'Assistente non disponibile' : 'Scrivi un messaggio…'}
                className="flex-1 resize-none max-h-24 px-3 py-2 text-sm rounded-xl border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#1C1C19]/20 disabled:bg-[#F3F3F0] disabled:text-[#9A9A96]"
              />
              <button
                type="button"
                onClick={send}
                disabled={sending || unavailable || !input.trim()}
                data-testid="ai-chat-send"
                aria-label="Invia"
                className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#1C1C19] text-white disabled:opacity-40 hover:opacity-90 transition-opacity shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AiChatWidget;
